'use client';

import { RefObject, useEffect, useState } from 'react';

const PAGE_SELECTOR = '[data-paged-carousel-page]';

const SCROLL_EDGE_TOLERANCE_PX = 2;

export function useActivePage(
  viewportRef: RefObject<HTMLUListElement | null>,
  pageCount: number
) {
  const [activePage, setActivePage] = useState(0);

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport || pageCount <= 1) {
      return;
    }

    const pages = Array.from(
      viewport.querySelectorAll<HTMLElement>(PAGE_SELECTOR)
    );

    if (pages.length === 0) {
      return;
    }

    const updateActivePage = () => {
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      const scrollDistance = Math.abs(viewport.scrollLeft);

      if (maxScroll <= 0 || scrollDistance <= SCROLL_EDGE_TOLERANCE_PX) {
        setActivePage(0);
        return;
      }

      if (scrollDistance >= maxScroll - SCROLL_EDGE_TOLERANCE_PX) {
        setActivePage(pages.length - 1);
        return;
      }

      const viewportRect = viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      pages.forEach((page, pageIndex) => {
        const pageRect = page.getBoundingClientRect();
        const pageCenter = pageRect.left + pageRect.width / 2;
        const distance = Math.abs(pageCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = pageIndex;
        }
      });

      setActivePage(closestIndex);
    };

    updateActivePage();

    viewport.addEventListener('scroll', updateActivePage, { passive: true });

    const resizeObserver = new ResizeObserver(updateActivePage);
    resizeObserver.observe(viewport);

    return () => {
      viewport.removeEventListener('scroll', updateActivePage);
      resizeObserver.disconnect();
    };
  }, [viewportRef, pageCount]);

  return activePage;
}

export function scrollToPage(
  viewportRef: RefObject<HTMLUListElement | null>,
  pageIndex: number
) {
  const viewport = viewportRef.current;

  if (!viewport) {
    return;
  }

  const page = viewport.querySelectorAll<HTMLElement>(PAGE_SELECTOR)[pageIndex];

  if (!page) {
    return;
  }

  const a11yPrefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  const isPageWiderThanViewport = page.offsetWidth > viewport.clientWidth;

  page.scrollIntoView({
    behavior: a11yPrefersReducedMotion ? 'auto' : 'smooth',
    inline: isPageWiderThanViewport ? 'start' : 'center',
    block: 'nearest',
  });
}
